import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'sonner'
import * as yup from 'yup'

import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { useAppDispatch } from '@/hooks'
import { ProgressBar } from '@/pages/customer/book-shelf'
import { CurrentBookingForm } from '@/pages/customer/extend-booking/current-bookings-form'
import { NewBookingForm } from '@/pages/customer/extend-booking/new-booking-form'
import { PaymentForm } from '@/pages/customer/extend-booking/payment-form'
import { Layout } from '@/pages/customer/layout'
import userService from '@/services/user-service'
import { setCurrentStore } from '@/slices/store'

import { LogoIcon, LogoImage } from '@/assets/icons'

enum FormSteps {
	'Aktuelle Buchung' = 0,
	'Neue Buchung',
	Zahlung,
}

export interface BookExtendForm {
	bookingId: string
	package: string
}

export const ExtendBooking = () => {
	const dispatch = useAppDispatch()
	const [currentStep, setCurrentStep] = useState<FormSteps>(FormSteps['Aktuelle Buchung'])
	const [showSuccessScreen, setShowSuccessScreen] = useState(false)

	const { data: currentBookings } = useQuery(
		'fetch-user-current-bookings',
		() =>
			userService.fetchUserCurrentBookings({
				pageNumber: '1',
				pageSize: '10000',
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const schema = yup.object<BookExtendForm>().shape({
		bookingId: yup.string().when('$step', {
			is: FormSteps['Aktuelle Buchung'],
			then: schema => schema.required('Please select a booking to extend'),
		}),
		package: yup.string().when('$step', {
			is: FormSteps['Neue Buchung'],
			then: schema => schema.required('Please select a package'),
		}),
	})

	const form = useForm<BookExtendForm>({
		resolver: yupResolver(schema as any),
		context: { step: currentStep },
		mode: 'all',
	})

	const selectedBooking = currentBookings?.find(
		booking => booking.id === Number(form.watch('bookingId')),
	) as UserBooking

	useEffect(() => {
		const store = selectedBooking?.store
		if (!store?.uuid) return
		dispatch(
			setCurrentStore({
				uuid: store.uuid,
				slug: store.slug,
				name: store.name,
				paypal_client_id: store.paypal_client_id,
			}),
		)
	}, [selectedBooking?.store, dispatch])

	const goToPrevStep = () => {
		setCurrentStep(prev => prev - 1)
	}

	const goToNextStep = form.handleSubmit(
		() => {
			setCurrentStep(prev => prev + 1)
		},
		errors => {
			if (errors.bookingId) {
				toast.error(errors.bookingId?.message)
			}
			if (errors.package) {
				toast.error(errors.package?.message)
			}
		},
	)

	return (
		<Layout title="Buchung Verlängern">
			<div className="mt-2 lg:mt-3 grow container mx-auto rounded-xl lg:rounded-[16px] px-4 max-lg:pb-3 max-lg:pt-8 lg:px-6 lg:py-10 bg-white flex flex-col">
				{!showSuccessScreen && (
					<ProgressBar
						steps={Object.keys(FormSteps).filter(key => isNaN(Number(key)))}
						currentStep={currentStep}
					/>
				)}
				<Form {...form}>
					<form
						className="flex flex-col grow gap-8 lg:gap-12 justify-between"
						onSubmit={event => goToNextStep(event)}>
						{currentStep === FormSteps['Aktuelle Buchung'] && (
							<CurrentBookingForm currentBookings={currentBookings} />
						)}
						{currentStep === FormSteps['Neue Buchung'] && (
							<NewBookingForm selectedBooking={selectedBooking} />
						)}
						{currentStep === FormSteps.Zahlung && !showSuccessScreen && (
							<PaymentForm
								selectedBooking={selectedBooking}
								onExtendConfirm={() => setShowSuccessScreen(true)}
							/>
						)}
						{showSuccessScreen && (
							<div className="grow self-stretch flex items-center mt-10 flex-col">
								<LogoIcon className="shrink-0 max-lg:hidden lg:size-44" />
								<img src={LogoImage} className="size-32 lg:hidden" />
								<p className="mt-4 lg:text-lg font-medium">Platz schaffen und Gutes tun</p>
								<div className="flex flex-col items-center gap-1 my-10">
									<p className="text-lg lg:text-2xl text-center font-bold">
										Deine Buchung wurde erfolgreich verlängert!
									</p>
									<p className="lg:text-lg text-center">
										Weitere Details zu deiner Buchung findest du in deiner Bestellbestätigung.
									</p>
								</div>
								<p className="text-center lg:text-lg">Wir freuen uns auch dich!</p>
							</div>
						)}
						{!showSuccessScreen && (
							<div className="flex items-center justify-center lg:justify-end gap-2 lg:gap-3">
								{currentStep > 0 && (
									<Button
										variant="outline"
										type="button"
										onClick={goToPrevStep}
										className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 border-secondary text-secondary font-extrabold">
										Zurück
									</Button>
								)}
								{currentStep !== FormSteps.Zahlung && (
									<Button
										variant="secondary"
										className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 flex items-center font-extrabold">
										Weiter
										<ChevronRightIcon className="size-4 shrink-0" />
									</Button>
								)}
							</div>
						)}
					</form>
				</Form>
			</div>
		</Layout>
	)
}
